/**
 * Orchestra AI Platform - Scenario Browser Component
 * 
 * Component for browsing and discovering educational scenarios
 * 
 * @fileoverview Scenario browser with search, filters, and categories
 */

import { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ScenarioCard } from './ScenarioCard';
import { supabase, supabaseAdmin } from '../../lib/supabase';
import {
  fetchAllScenarios,
  fetchPopularScenarios,
  fetchScenariosByDifficulty,
  searchScenarios as searchScenariosService,
} from '../../services/scenariosService';
import toast from 'react-hot-toast';
import './ScenarioBrowser.css';

/**
 * Scenario Browser Component
 * @param {Object} props
 * @param {Function} [props.onScenarioSelect] - Handler for scenario selection
 */
export function ScenarioBrowser({ onScenarioSelect, onTabChange }) {
  const [category, setCategory] = useState('all');
  const [difficulty, setDifficulty] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('all'); // all, popular, beginner
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);

  // Load scenarios from Supabase
  useEffect(() => {
    loadScenarios();
  }, []);

  /**
   * Load scenarios from Supabase
   */
  const loadScenarios = async () => {
    try {
      setLoading(true);
      const allScenarios = await fetchAllScenarios();
      setScenarios(allScenarios);
      
      // Extract unique categories
      const uniqueCategories = [...new Set(allScenarios.map(s => s.category))];
      setCategories(uniqueCategories);
    } catch (error) {
      console.error('[Scenario Browser] Failed to load scenarios:', error);
      toast.error('Failed to load scenarios');
    } finally {
      setLoading(false);
    }
  };

  // Filter scenarios
  const filteredScenarios = useMemo(() => {
    let filtered = scenarios;

    // Apply view mode filter
    if (viewMode === 'popular') {
      filtered = filtered.filter((s) => s.is_popular === true);
    } else if (viewMode === 'beginner') {
      filtered = filtered.filter((s) => s.difficulty === 'beginner');
    }

    // Apply category filter
    if (category !== 'all') {
      filtered = filtered.filter((s) => s.category === category);
    }

    // Apply difficulty filter
    if (difficulty.length > 0) {
      filtered = filtered.filter((s) => difficulty.includes(s.difficulty));
    }

    // Apply search
    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (s) =>
          s.name.toLowerCase().includes(lowerQuery) ||
          s.description.toLowerCase().includes(lowerQuery) ||
          (s.use_cases && s.use_cases.some((uc) => uc.toLowerCase().includes(lowerQuery)))
      );
    }

    return filtered;
  }, [scenarios, category, difficulty, searchQuery, viewMode]);

  /**
   * Apply scenario template
   * @param {Object} scenario
   */
  const applyScenarioTemplate = async (scenario) => {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        toast.error('You must be logged in to use scenarios');
        return;
      }

      console.log('[Scenario Browser] Starting template application for:', scenario.name);
      console.log('[Scenario Browser] User ID:', user.id);
      console.log('[Scenario Browser] Scenario data:', scenario);
      console.log('[Scenario Browser] Workflow template:', scenario.workflow_template);

      // Check if workflow_template exists and has nodes
      if (!scenario.workflow_template) {
        console.warn('[Scenario Browser] No workflow_template found, using default');
        scenario.workflow_template = {
          nodes: [],
          edges: [],
          viewport: { x: 0, y: 0, zoom: 1 }
        };
      } else {
        // Log template structure for debugging
        console.log('[Scenario Browser] Template structure:', {
          hasNodes: !!scenario.workflow_template.nodes,
          nodeCount: scenario.workflow_template.nodes?.length || 0,
          hasEdges: !!scenario.workflow_template.edges,
          edgeCount: scenario.workflow_template.edges?.length || 0,
          hasViewport: !!scenario.workflow_template.viewport
        });
      }

      // Increment usage count (non-blocking) - use admin client
      try {
        await supabaseAdmin
          .from('developer_scenarios')
          .update({ usage_count: (scenario.usage_count || 0) + 1 })
          .eq('id', scenario.id);
      } catch (err) {
        console.warn('[Scenario Browser] Failed to increment usage count:', err);
        // Continue even if this fails
      }

      // Create new project from scenario - use RPC function to bypass RLS
      console.log('[Scenario Browser] Creating project via RPC...');
      let project;
      
      // Try using RPC function first (bypasses RLS and returns full project data)
      const { data: projectData_rpc, error: rpcError } = await supabase.rpc('create_developer_project', {
        p_user_id: user.id,
        p_name: scenario.name,
        p_description: scenario.description || '',
        p_type: 'cross_subject',
        p_status: 'active',
        p_settings: {
          auto_save: true,
          collaboration_enabled: false,
          default_timeout: 30000,
        }
      });

      if (rpcError || !projectData_rpc) {
        console.warn('[Scenario Browser] RPC function not available, trying direct insert...', rpcError);
        // Fallback to direct insert with admin client (bypasses RLS)
        console.log('[Scenario Browser] Using admin client to bypass RLS...');
        const { data: adminProject, error: adminProjectError } = await supabaseAdmin
          .from('developer_projects')
          .insert({
            user_id: user.id,
            name: scenario.name,
            description: scenario.description || '',
            type: 'cross_subject',
            status: 'active',
            settings: {
              auto_save: true,
              collaboration_enabled: false,
              default_timeout: 30000,
            },
          })
          .select()
          .single();

        if (adminProjectError) {
          console.error('[Scenario Browser] Admin project creation error:', adminProjectError);
          toast.error('Failed to create project. Please run QUICK_FIX_BYPASS_RLS.sql in Supabase SQL Editor.');
          throw adminProjectError;
        }
        
        project = adminProject;
      } else {
        // RPC succeeded and returned full project data (no need to fetch)
        // Handle JSONB response - it might be a string or already parsed
        if (typeof projectData_rpc === 'string') {
          project = JSON.parse(projectData_rpc);
        } else {
          project = projectData_rpc;
        }
        console.log('[Scenario Browser] Project created via RPC:', project);
      }

      console.log('[Scenario Browser] Project created successfully:', project.id);

      // Create workflow from template - use RPC function to bypass RLS
      console.log('[Scenario Browser] Creating workflow via RPC...');
      
      // Ensure workflow_template has proper structure with viewport
      const templateData = scenario.workflow_template || {
        nodes: [],
        edges: [],
        viewport: { x: 0, y: 0, zoom: 1 }
      };
      
      // Ensure all required fields exist
      if (!templateData.nodes || !Array.isArray(templateData.nodes)) {
        templateData.nodes = [];
      }
      if (!templateData.edges || !Array.isArray(templateData.edges)) {
        templateData.edges = [];
      }
      if (!templateData.viewport) {
        templateData.viewport = { x: 0, y: 0, zoom: 1 };
      }
      
      console.log('[Scenario Browser] Template data to save:', {
        nodeCount: templateData.nodes.length,
        edgeCount: templateData.edges.length,
        viewport: templateData.viewport,
        sampleNodes: templateData.nodes.slice(0, 2) // Log first 2 nodes for debugging
      });
      
      const workflowData = {
        project_id: project.id,
        name: 'Main Workflow',
        description: scenario.description || '',
        workflow_data: templateData,
        is_template: false,
        status: 'draft',
      };

      console.log('[Scenario Browser] Workflow data to insert:', {
        project_id: workflowData.project_id,
        name: workflowData.name,
        workflow_data_node_count: workflowData.workflow_data.nodes.length,
        workflow_data_edge_count: workflowData.workflow_data.edges.length
      });

      let workflow;
      // Try using RPC function first (bypasses RLS and returns full workflow data)
      const { data: workflowData_rpc, error: rpcWorkflowError } = await supabase.rpc('create_developer_workflow', {
        p_project_id: project.id,
        p_name: 'Main Workflow',
        p_description: scenario.description || '',
        p_workflow_data: workflowData.workflow_data,
        p_is_template: false,
        p_status: 'draft'
      });

      if (rpcWorkflowError || !workflowData_rpc) {
        console.warn('[Scenario Browser] RPC workflow function not available, using admin client...', rpcWorkflowError);
        // Fallback to admin client (bypasses RLS)
        const { data: adminWorkflow, error: adminWorkflowError } = await supabaseAdmin
          .from('developer_workflows')
          .insert(workflowData)
          .select()
          .single();

        if (adminWorkflowError) {
          console.error('[Scenario Browser] Admin workflow creation error:', adminWorkflowError);
          toast.error('Failed to create workflow. Please run QUICK_FIX_BYPASS_RLS.sql in Supabase SQL Editor.');
          throw adminWorkflowError;
        }

        workflow = adminWorkflow;
      } else {
        // RPC succeeded and returned full workflow data (no need to fetch)
        // Handle JSONB response - it might be a string or already parsed
        if (typeof workflowData_rpc === 'string') {
          workflow = JSON.parse(workflowData_rpc);
        } else {
          workflow = workflowData_rpc;
        }
        console.log('[Scenario Browser] Workflow created via RPC:', workflow);
      }

      console.log('[Scenario Browser] Workflow created successfully:', workflow.id);
      console.log('[Scenario Browser] Final workflow data:', {
        workflowId: workflow.id,
        projectId: project.id,
        nodeCount: templateData.nodes.length,
        edgeCount: templateData.edges.length
      });
      
      // Trigger a custom event to refresh workspace projects list
      window.dispatchEvent(new CustomEvent('projectCreated', { 
        detail: { projectId: project.id, projectName: project.name } 
      }));
      
      toast.success(`Template "${scenario.name}" loaded! Opening workflow builder...`);
      
      // Navigate to workflow builder with the new workflow
      if (onTabChange) {
        onTabChange('workflow-builder', { 
          workflowId: workflow.id,
          projectId: project.id 
        });
      } else if (onScenarioSelect) {
        onScenarioSelect(scenario.id);
      }
    } catch (error) {
      console.error('[Scenario Browser] Failed to use template:', error);
      if (!error.message || !error.message.includes('Failed to')) {
        toast.error('Failed to use template: ' + (error.message || 'Unknown error'));
      }
    }
  };

  /**
   * View scenario details
   * @param {Object} scenario
   */
  const viewScenarioDetails = (scenario) => {
    if (onScenarioSelect) {
      onScenarioSelect(scenario.id);
    }
  };

  // Group scenarios by category for display
  const scenariosByCategory = useMemo(() => {
    return categories.reduce((acc, cat) => {
      acc[cat] = scenarios.filter((s) => s.category === cat);
      return acc;
    }, {});
  }, [scenarios, categories]);

  if (loading) {
    return (
      <div className="scenario-browser">
        <div className="loading-state">
          <p>Loading scenarios...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="scenario-browser">
      {/* Header */}
      <div className="header">
        <div className="header-content">
          <h1>Educational Scenarios</h1>
          <div className="count">
            {filteredScenarios.length} of {scenarios.length} scenarios
          </div>
        </div>

        {/* Search */}
        <div className="search-container">
          <input
            type="text"
            placeholder="Search scenarios by name, description, or use case..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        {/* Filters */}
        <div className="filters">
          {/* View Mode */}
          <div className="filter-group">
            <button
              onClick={() => setViewMode('all')}
              className={`filter-button ${viewMode === 'all' ? 'active' : ''}`}
            >
              All
            </button>
            <button
              onClick={() => setViewMode('popular')}
              className={`filter-button ${viewMode === 'popular' ? 'active' : ''}`}
            >
              Popular
            </button>
            <button
              onClick={() => setViewMode('beginner')}
              className={`filter-button ${viewMode === 'beginner' ? 'active' : ''}`}
            >
              Beginner
            </button>
          </div>

          {/* Category Filter */}
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Categories</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {cat.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())} (
                {scenariosByCategory[cat]?.length || 0})
              </option>
            ))}
          </select>

          {/* Difficulty Filter */}
          <div className="filter-group">
            {['beginner', 'intermediate', 'advanced'].map((diff) => (
              <button
                key={diff}
                onClick={() => {
                  setDifficulty((prev) =>
                    prev.includes(diff)
                      ? prev.filter((d) => d !== diff)
                      : [...prev, diff]
                  );
                }}
                className={`filter-button ${difficulty.includes(diff) ? 'active' : ''}`}
              >
                {diff.charAt(0).toUpperCase() + diff.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="content">
        {filteredScenarios.length === 0 ? (
          <div className="empty-state">
            <p>No scenarios found</p>
            <p className="hint">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="scenarios-grid">
            {filteredScenarios.map((scenario) => (
              <ScenarioCard
                key={scenario.id}
                scenario={scenario}
                onUse={() => applyScenarioTemplate(scenario)}
                onView={() => viewScenarioDetails(scenario)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

